<script setup lang="ts">
import { toRef } from 'vue'

const props = withDefaults(defineProps<{ modelValue?: boolean; label?: string; disabled?: boolean, position?: 'right' | 'left', indeterminate?: boolean }>(), {
  modelValue: false,
  disabled: false,
  position: 'right',
  indeterminate: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', checked: boolean): void
}>()

const instanceId = `_${Math.random().toString(15).substr(2, 9)}`
const label = toRef(props, 'label')
const updateCheckbox = ($event) => {
  emit('update:modelValue', $event.target.checked)
}
</script>

<template>
  <div class="inner" @click.stop>
    <input :id="instanceId" :class="{ 'cursor-not-allowed!': disabled }" :disabled="disabled" type="checkbox" :checked="modelValue" @change="updateCheckbox">
    <label :for="instanceId" class="flex items-center gap-1" :class="{ 'reverse': !modelValue && !indeterminate, 'cursor-not-allowed!': disabled, 'flex-row-reverse': position === 'left', 'indeterminate': indeterminate }">
      <svg viewBox="0 0 100 100">
        <path
          class="box"
          d="M82,89H18c-3.87,0-7-3.13-7-7V18c0-3.87,3.13-7,7-7h64c3.87,0,7,3.13,7,7v64C89,85.87,85.87,89,82,89z"
        />
        <polyline class="check" points="25.5,53.5 39.5,67.5 72.5,34.5 " />
        <line class="minus" x1="25" y1="50" x2="75" y2="50" />
      </svg>
      <slot><span>{{ label }}</span></slot>
    </label>
  </div>
</template>

<style scoped>
* {
  box-sizing: border-box;
}

input[type="checkbox"] {
  display: none;
  pointer-events: none;
}

input[type="checkbox"] + label {
  cursor: pointer;
  font-size: 0.8em;
}

input[type="checkbox"] + label svg {
  width: 2em;
  flex-shrink: 0;
  stroke: var(--color-primary);
  stroke-width: 5;
}

.dark input[type="checkbox"] + label svg .box {
  fill: #272727
}

input[type="checkbox"] + label svg .box {
  stroke-dasharray: 320;
  stroke-dashoffset: 0;
  fill: white;
}

input[type="checkbox"] + label svg .check {
  stroke-dasharray: 70;
  stroke-dashoffset: 70;
  fill: none;
}

input[type="checkbox"] + label svg .minus {
  stroke-dasharray: 50;
  stroke-dashoffset: 50;
  stroke-width: 8;
  fill: none;
}

input[type="checkbox"] + label span {
  padding-top: 0.3em;
  margin-left: 0.3em;
}

input[type="checkbox"]:checked + label .box {
  stroke-dashoffset: 320;
  transition: stroke-dashoffset 0.3s linear;
}

input[type="checkbox"]:checked + label .check {
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 0.3s linear;
}

.reverse .box {
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 0.3s linear;
}

.reverse .check {
  stroke-dashoffset: 70;
  transition: stroke-dashoffset 0.3s linear;
}

.reverse .minus {
  stroke-dashoffset: 50;
  transition: stroke-dashoffset 0.3s linear;
}

label.indeterminate .box {
  stroke-dashoffset: 320 !important;
  transition: stroke-dashoffset 0.3s linear;
}

label.indeterminate .check {
  stroke-dashoffset: 70 !important;
}

label.indeterminate .minus {
  stroke-dashoffset: 0 !important;
  transition: stroke-dashoffset 0.3s linear;
}
</style>
